import { formatDate, parseDate } from '@travelata-libs/utils';
import Cookies from 'js-cookie';
import { useTravelataApiService } from '@/shared/composables/useTravelataApiService';
import { useTravelataAppConfig } from '@/shared/composables/useTravelataAppConfig';
import { toTouristGroupParams } from '@/shared/lib/touristGroupParams';
import type { SearchCriteriaParams } from '@/shared/model';
import { serializeSearchParams } from '../productSearch';
import type { LowPriceDay, SearchLatestParams, SearchLatestResponse } from './types';

const ENDPOINT = '/frontend/prices/searchLatest';

const SHADOW_MARKUP_COOKIE_KEY = 'tr_sm';

const API_DATE_FORMAT = 'yyyy-MM-dd';

function toApiDate(value: string | undefined): string | undefined {
  if (!value) return undefined;

  const parsed = parseDate(value);

  return parsed ? formatDate(parsed, API_DATE_FORMAT) : value;
}

function toNonEmpty<T>(values: T[] | undefined): T[] | undefined {
  return values?.length ? values : undefined;
}

function toRange<T extends string | number>(range: { from?: T; to?: T } | undefined): { from?: T; to?: T } | undefined {
  if (!range?.from && !range?.to) return undefined;

  return { ...(range.from && { from: range.from }), ...(range.to && { to: range.to }) };
}

export function buildSearchLatestParams(params: SearchCriteriaParams): SearchLatestParams {
  const checkInDateRange = toRange({
    from: toApiDate(params.checkInDateRange?.from),
    to: toApiDate(params.checkInDateRange?.to),
  });
  const nightRange = toRange(params.nightRange);
  const countries = toNonEmpty(params.countries);
  const resorts = toNonEmpty(params.resorts);
  const hotels = toNonEmpty(params.hotels);
  const meals = toNonEmpty(params.meals);
  const hotelCategories = toNonEmpty(params.hotelCategories);
  const trSm = Cookies.get(SHADOW_MARKUP_COOKIE_KEY);

  return {
    ...(checkInDateRange && { checkInDateRange }),
    ...(countries && { countries }),
    ...(params.departureCity?.id && { departureCity: params.departureCity.id }),
    ...(resorts && { resorts }),
    ...(hotels && { hotels }),
    ...(nightRange && { nightRange }),
    ...(meals && { meals }),
    ...(hotelCategories && { hotelCategories }),
    touristGroup: toTouristGroupParams(params.touristGroup),
    ...(trSm && { trSm }),
  };
}

function toLowPriceDays(result: SearchLatestResponse['result']): LowPriceDay[] {
  if (!Array.isArray(result)) return [];

  return result.reduce<LowPriceDay[]>((days, day) => {
    const date = toApiDate(day?.checkindate);

    if (date) {
      days.push({ date, price: day?.price ? Math.round(day.price) : null });
    }

    return days;
  }, []);
}

export async function searchLatest(params: SearchCriteriaParams, signal?: AbortSignal): Promise<LowPriceDay[] | null> {
  if (!params.countries?.length) return [];

  try {
    const apiService = useTravelataApiService();
    const config = useTravelataAppConfig();
    const query = serializeSearchParams(buildSearchLatestParams(params));
    const response = await apiService.get<SearchLatestResponse>(`${config.apiGatewayUrl}${ENDPOINT}?${query}`, {
      signal,
    });

    if (response?.success !== true) return null;

    return toLowPriceDays(response.result);
  } catch {
    return null;
  }
}
